import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  //IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  AlertColor
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UploadIcon from '@mui/icons-material/Upload';
import { createTimestamp } from '../../../utils/firebaseUtils';
import { surgicalCaseService } from '../../../services/surgicalCaseService';
import { surgicalPhasesService } from '../../../services/surgicalPhasesService';
import { addLabOrder } from '../../../services/labOrdersService';
import { useUserData } from '../../../hooks/useUserData';

interface SurgicalProcedureData {
  id: string;
  procedureType: string;
  specialty: string;
  status: string;
  orderDate: Date;
  estimatedDuration?: number;
  requiresConsent?: boolean;
  specialInstructions?: string;
  orderingProvider?: string;
  priority?: string;
}

interface Patient {
  id: string;
  name: string;
  mrn?: string;
}

interface SurgicalPreOpPhaseProps {
  surgicalProcedure: SurgicalProcedureData;
  patient: Patient;
  encounterId?: string;
}

const SurgicalPreOpPhase: React.FC<SurgicalPreOpPhaseProps> = ({ 
  surgicalProcedure, 
  patient, 
  encounterId 
}) => {
  const { user } = useUserData();
  const [checklistStatus, setChecklistStatus] = useState({
    consentForms: 'pending',
    preOpAssessment: 'pending',
    anesthesiaConsult: 'pending',
    preOpLabs: 'pending',
    preOpEducation: 'pending'
  });

  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: AlertColor }>({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });
  const [assessmentNotes, setAssessmentNotes] = useState('');
  const [educationNotes, setEducationNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const showSnackbar = (message: string, severity: AlertColor = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleUploadConsent = async () => {
    try {
      // Create file input for consent upload
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          setLoading(true);
          try {
            // Prepare consent data for service
            const consentData = {
              surgicalProcedureId: surgicalProcedure.id,
              patientId: patient.id,
              encounterId: encounterId || null,
              fileName: file.name,
              fileSize: file.size,
              consentType: 'surgical_consent'
            };

            // Use service to upload consent
            const consentRecord = await surgicalPhasesService.uploadConsentForm(consentData);

            // Update surgical case checklist
            if (encounterId) {
              const caseId = `case_${surgicalProcedure.id}`;
              await surgicalCaseService.updateCasePhase(caseId, 'preOp', {
                status: 'in-progress',
                checklist: {
                  consentForms: {
                    status: 'completed',
                    completedAt: new Date(),
                    completedBy: user?.uid,
                    fileUrl: (consentRecord as any).fileUrl,
                    fileName: (consentRecord as any).fileName
                  }
                }
              });
            }

            setChecklistStatus(prev => ({ ...prev, consentForms: 'completed' }));
            showSnackbar(`Consent file "${file.name}" uploaded successfully`);
          } catch (error) {
            console.error('Error uploading consent:', error);
            showSnackbar('Failed to upload consent file. Please try again.', 'error');
          } finally {
            setLoading(false);
          }
        }
      };
      input.click();
    } catch (error) {
      console.error('Error setting up file upload:', error);
      showSnackbar('Failed to open file picker', 'error');
    }
  };

  const handleMarkConsentComplete = async () => {
    try {
      setLoading(true);
      
      // Prepare consent completion data
      const consentData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        method: 'manual_completion',
        notes: 'Consent marked as completed manually'
      };

      // Use service to mark consent complete
      await surgicalPhasesService.markConsentComplete(consentData);

      // Update surgical case checklist
      if (encounterId) {
        const caseId = `case_${surgicalProcedure.id}`;
        await surgicalCaseService.updateCasePhase(caseId, 'preOp', {
          status: 'in-progress',
          checklist: {
            consentForms: {
              status: 'completed',
              completedAt: new Date(),
              completedBy: user?.uid,
              method: 'manual_completion'
            }
          }
        });
      }

      setChecklistStatus(prev => ({ ...prev, consentForms: 'completed' }));
      showSnackbar('Surgical consent forms marked as completed');
    } catch (error) {
      console.error('Error marking consent as complete:', error);
      showSnackbar('Failed to mark consent as complete', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteAssessment = () => {
    setOpenDialog('assessment');
  };

  const handleSaveAssessment = async () => {
    if (!assessmentNotes.trim()) {
      showSnackbar('Please enter assessment notes', 'error');
      return;
    }

    try {
      setLoading(true);

      // Prepare assessment data
      const assessmentData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        assessmentNotes: assessmentNotes,
        assessmentType: 'nursing_assessment'
      };

      // Use service to create assessment
      await surgicalPhasesService.createPreOpAssessment(assessmentData);

      // Update surgical case checklist
      if (encounterId) {
        const caseId = `case_${surgicalProcedure.id}`;
        await surgicalCaseService.updateCasePhase(caseId, 'preOp', {
          status: 'in-progress',
          checklist: {
            preOpAssessment: {
              status: 'completed',
              completedAt: new Date(),
              completedBy: user?.uid,
              notes: assessmentNotes
            }
          }
        });
      }

      setChecklistStatus(prev => ({ ...prev, preOpAssessment: 'completed' }));
      setOpenDialog(null);
      setAssessmentNotes('');
      showSnackbar('Pre-op assessment completed successfully');
    } catch (error) {
      console.error('Error saving assessment:', error);
      showSnackbar('Failed to save assessment', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleConsult = async () => {
    try {
      setLoading(true);

      // Create anesthesia consultation appointment
      const consultationDate = new Date();
      consultationDate.setHours(consultationDate.getHours() + 24); // Schedule for tomorrow

      // Prepare consultation data
      const consultationData = {
        patientId: patient.id,
        patientName: patient.name,
        patientMRN: patient.mrn,
        encounterId: encounterId || null,
        appointmentType: 'anesthesia_consultation',
        purpose: `Pre-operative anesthesia consultation for ${surgicalProcedure.procedureType}`,
        date: consultationDate,
        duration: 30, // 30 minutes
        provider: 'Anesthesiology Team',
        department: 'Anesthesiology',
        notes: `Pre-op consult for surgical procedure: ${surgicalProcedure.procedureType}`,
        surgicalProcedureId: surgicalProcedure.id,
        priority: surgicalProcedure.priority || 'routine'
      };

      // Use service to schedule consultation
      await surgicalPhasesService.scheduleAnesthesiaConsultation(consultationData);

      // Update surgical case checklist
      if (encounterId) {
        const caseId = `case_${surgicalProcedure.id}`;
        await surgicalCaseService.updateCasePhase(caseId, 'preOp', {
          status: 'in-progress',
          checklist: {
            anesthesiaConsult: {
              status: 'completed',
              completedAt: new Date(),
              completedBy: user?.uid,
              scheduledDate: consultationDate,
              notes: 'Anesthesia consultation scheduled'
            }
          }
        });
      }

      setChecklistStatus(prev => ({ ...prev, anesthesiaConsult: 'completed' }));
      showSnackbar('Anesthesia consultation scheduled successfully');
    } catch (error) {
      console.error('Error scheduling consultation:', error);
      showSnackbar('Failed to schedule consultation', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOrderLabs = async () => {
    try {
      setLoading(true);

      // Define standard pre-op lab orders
      const preOpLabOrders = [
        {
          testCode: 'CBC',
          name: 'Complete Blood Count',
          description: 'Pre-operative CBC',
          category: 'Hematology'
        },
        {
          testCode: 'BMP',
          name: 'Basic Metabolic Panel',
          description: 'Pre-operative chemistry panel',
          category: 'Chemistry'
        },
        {
          testCode: 'PT_PTT',
          name: 'PT/PTT/INR',
          description: 'Coagulation studies',
          category: 'Coagulation'
        }
      ];

      // Create lab orders
      const labOrderPromises = preOpLabOrders.map(labTest => 
        addLabOrder({
          patientId: patient.id,
          encounterId: encounterId || null,
          testType: 'Lab',
          testCode: labTest.testCode,
          name: labTest.name,
          description: labTest.description,
          category: labTest.category,
          priority: 'routine',
          orderingProvider: user?.name || 'Unknown Provider',
          orderingFacility: 'Hospital',
          surgicalProcedureId: surgicalProcedure.id,
          indication: `Pre-operative labs for ${surgicalProcedure.procedureType}`,
          specimenType: 'Blood',
          fastingRequired: false,
          userId: user?.uid
        })
      );

      await Promise.all(labOrderPromises);

      // Update surgical case checklist
      if (encounterId) {
        const caseId = `case_${surgicalProcedure.id}`;
        await surgicalCaseService.updateCasePhase(caseId, 'preOp', {
          status: 'in-progress',
          checklist: {
            preOpLabs: {
              status: 'completed',
              completedAt: createTimestamp(),
              completedBy: user?.uid,
              ordersPlaced: preOpLabOrders.length,
              notes: 'Standard pre-op lab orders placed'
            }
          }
        });
      }

      setChecklistStatus(prev => ({ ...prev, preOpLabs: 'completed' }));
      showSnackbar(`${preOpLabOrders.length} pre-op lab orders placed successfully`);
    } catch (error) {
      console.error('Error ordering labs:', error);
      showSnackbar('Failed to place lab orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentEducation = () => {
    setOpenDialog('education');
  };

  const handleSaveEducation = async () => {
    if (!educationNotes.trim()) {
      showSnackbar('Please enter education documentation', 'error');
      return;
    }

    try {
      setLoading(true);

      // Prepare education data
      const educationData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        educationType: 'pre_operative_education',
        educationNotes: educationNotes,
        topicsCovered: [
          'Pre-operative instructions',
          'Post-operative care',
          'Pain management',
          'Activity restrictions',
          'Follow-up appointments'
        ],
        teachBackMethod: true,
        patientUnderstanding: 'demonstrated'
      };

      // Use service to document education
      await surgicalPhasesService.documentPatientEducation(educationData);

      // Update surgical case checklist
      if (encounterId) {
        const caseId = `case_${surgicalProcedure.id}`;
        await surgicalCaseService.updateCasePhase(caseId, 'preOp', {
          status: 'in-progress',
          checklist: {
            preOpEducation: {
              status: 'completed',
              completedAt: new Date(),
              completedBy: user?.uid,
              educationNotes: educationNotes,
              educatorName: user?.name
            }
          }
        });
      }

      setChecklistStatus(prev => ({ ...prev, preOpEducation: 'completed' }));
      setOpenDialog(null);
      setEducationNotes('');
      showSnackbar('Patient education documented successfully');
    } catch (error) {
      console.error('Error saving education documentation:', error);
      showSnackbar('Failed to save education documentation', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPreOpComplete = async () => {
    const incomplete = Object.entries(checklistStatus).filter(([_, status]) => status !== 'completed');
    if (incomplete.length > 0) {
      showSnackbar('Please complete all checklist items before marking pre-op as complete', 'error');
      return;
    }

    try {
      setLoading(true);

      // Update surgical case status to pre-op complete
      if (encounterId) {
        const caseId = `case_${surgicalProcedure.id}`;
        await surgicalCaseService.updateCasePhase(caseId, 'preOp', {
          status: 'completed',
          completedAt: createTimestamp(),
          completedBy: user?.uid
        });

        await surgicalCaseService.updateCaseStatus(caseId, 'ready_for_surgery', 'intraOp');
      }

      // Prepare completion data
      const completionData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        checklistItems: checklistStatus,
        notes: 'All pre-operative requirements completed successfully'
      };

      // Use service to create completion record
      await surgicalPhasesService.createPreOpCompletion(completionData);

      showSnackbar('Pre-op phase marked as complete - Ready for surgery');
    } catch (error) {
      console.error('Error marking pre-op complete:', error);
      showSnackbar('Failed to mark pre-op as complete', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintPreOpPacket = async () => {
    try {
      setLoading(true);

      // Prepare print request data
      const printData = {
        documentType: 'pre_op_packet',
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null
      };

      // Use service to create print request
      await surgicalPhasesService.createPrintRequest(printData);

      // In a real implementation, this would trigger a print job
      // For now, we'll simulate the printing process
      showSnackbar('Pre-op packet sent to printer successfully');
    } catch (error) {
      console.error('Error printing pre-op packet:', error);
      showSnackbar('Failed to send packet to printer', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleSurgery = async () => {
    try {
      setLoading(true);

      // Create OR scheduling request
      const surgeryDate = new Date();
      surgeryDate.setDate(surgeryDate.getDate() + 1); // Schedule for tomorrow
      surgeryDate.setHours(8, 0, 0, 0); // 8:00 AM

      // Prepare scheduling request data
      const schedulingData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        patientName: patient.name,
        patientMRN: patient.mrn,
        encounterId: encounterId || null,
        procedureType: surgicalProcedure.procedureType,
        specialty: surgicalProcedure.specialty,
        estimatedDuration: surgicalProcedure.estimatedDuration || 120,
        preferredDate: surgeryDate,
        priority: surgicalProcedure.priority || 'routine',
        orderingProvider: surgicalProcedure.orderingProvider,
        notes: 'Pre-op completed, ready for OR scheduling'
      };

      // Use service to create scheduling request
      await surgicalPhasesService.createORSchedulingRequest(schedulingData);

      showSnackbar('Surgery scheduling request submitted to OR coordination');
    } catch (error) {
      console.error('Error requesting surgery scheduling:', error);
      showSnackbar('Failed to submit scheduling request', 'error');
    } finally {
      setLoading(false);
    }
  };

  const checklist = [
    {
      key: 'consentForms',
      title: 'Surgical Consent Forms',
      status: checklistStatus.consentForms,
      description: 'Pending completion and signature',
      action: (
        <Stack direction="row" spacing={2}>
          <Button 
            variant="contained" 
            color="inherit" 
            size="small"
            startIcon={<UploadIcon />}
            onClick={handleUploadConsent}
            disabled={loading}
          >
            Upload Signed Consent
          </Button>
          <Button 
            variant="contained" 
            color="success" 
            size="small"
            onClick={handleMarkConsentComplete}
            disabled={loading}
          >
            Mark as Completed
          </Button>
        </Stack>
      )
    },
    {
      key: 'preOpAssessment',
      title: 'Pre-Op Assessment',
      status: checklistStatus.preOpAssessment,
      description: 'Nursing assessment required',
      action: (
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={handleCompleteAssessment}
          disabled={loading}
        >
          Complete Assessment
        </Button>
      )
    },
    {
      key: 'anesthesiaConsult',
      title: 'Anesthesia Consultation',
      status: checklistStatus.anesthesiaConsult,
      description: 'Anesthesia team evaluation needed',
      action: (
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={handleScheduleConsult}
          disabled={loading}
        >
          Schedule Consult
        </Button>
      )
    },
    {
      key: 'preOpLabs',
      title: 'Pre-Op Laboratory Studies',
      status: checklistStatus.preOpLabs,
      description: 'Required lab work per protocol',
      action: (
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={handleOrderLabs}
          disabled={loading}
        >
          Order Labs
        </Button>
      )
    },
    {
      key: 'preOpEducation',
      title: 'Patient Education',
      status: checklistStatus.preOpEducation,
      description: 'Pre/post-op instructions provided',
      action: (
        <Button 
          variant="contained" 
          color="success" 
          size="small"
          onClick={handleDocumentEducation}
          disabled={loading}
        >
          Document Education
        </Button>
      )
    }
  ];

  const statusIcon = (status: string) => {
    if (status === 'completed') {
      return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32, mr: 2 }} />;
    }
    return <CancelIcon sx={{ color: 'error.main', fontSize: 32, mr: 2 }} />;
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Status Bar */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoOutlinedIcon sx={{ color: 'warning.main', mr: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Pre-Op Status: {surgicalProcedure.status === 'ordered' ? 'Scheduled' : 'Pending'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Procedure: {surgicalProcedure.procedureType}
          </Typography>
        </CardContent>
      </Card>

      {/* Procedure Information */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Procedure Information
          </Typography>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Procedure Type:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{surgicalProcedure.procedureType}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Specialty:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{surgicalProcedure.specialty}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Ordering Provider:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {surgicalProcedure.orderingProvider || 'Not specified'}
              </Typography>
            </Box>
            {surgicalProcedure.estimatedDuration && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Estimated Duration:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {surgicalProcedure.estimatedDuration} minutes
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Priority:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {surgicalProcedure.priority || 'Routine'}
              </Typography>
            </Box>
          </Stack>
          {surgicalProcedure.specialInstructions && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Special Instructions:
              </Typography>
              <Typography variant="body2">
                {surgicalProcedure.specialInstructions}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Pre-Op Checklist */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Pre-Op Checklist
      </Typography>
      <Stack spacing={2}>
        {checklist.map(item => (
          <Card key={item.key} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {statusIcon(item.status)}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.status === 'completed' ? 'Completed' : item.description}
                  </Typography>
                </Box>
              </Box>
              <Box>
                {item.action}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Sticky Footer Actions */}
      <Box sx={{
        position: 'sticky',
        bottom: 0,
        left: 0,
        width: '100%',
        background: '#fff',
        borderTop: '1px solid #e0e0e0',
        mt: 4,
        py: 2,
        zIndex: 10
      }}>
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Button 
            variant="contained" 
            color="success" 
            sx={{ minWidth: 180 }}
            onClick={handleMarkPreOpComplete}
            disabled={loading}
          >
            Mark Pre-Op Complete
          </Button>
          <Button 
            variant="contained" 
            color="inherit" 
            sx={{ minWidth: 180 }}
            onClick={handlePrintPreOpPacket}
            disabled={loading}
          >
            Print Pre-Op Packet
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            sx={{ minWidth: 140 }}
            onClick={handleScheduleSurgery}
            disabled={loading}
          >
            Schedule Surgery
          </Button>
        </Stack>
      </Box>

      {/* Assessment Dialog */}
      <Dialog open={openDialog === 'assessment'} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Complete Pre-Op Assessment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Assessment Notes"
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            value={assessmentNotes}
            onChange={(e) => setAssessmentNotes(e.target.value)}
            placeholder="Enter pre-operative assessment notes, vital signs, physical exam findings, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={loading}>Cancel</Button>
          <Button onClick={handleSaveAssessment} variant="contained" disabled={loading}>
            Save Assessment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Education Dialog */}
      <Dialog open={openDialog === 'education'} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Document Patient Education</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Education Documentation"
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            value={educationNotes}
            onChange={(e) => setEducationNotes(e.target.value)}
            placeholder="Document pre-operative and post-operative instructions provided to patient and family..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={loading}>Cancel</Button>
          <Button onClick={handleSaveEducation} variant="contained" disabled={loading}>
            Save Documentation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SurgicalPreOpPhase; 